import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerContent,
  VStack,
  Image,
  Text,
  Box,
  Flex,
  Input,
  Avatar,
  HStack
} from '@chakra-ui/react';
import TwygsLogoIcon from '../../../../assets/twyg-logo-icon.svg';
import UserAvatar from '../../../../assets/medium_default_optimized.png';
import { useAtom } from 'jotai/index';
import { conserve } from '../../../../apis/article-viewer';
import LexicalViewerTwygsBot from '../../LexicalViewerTwygsBot';
import { profileAtom } from '../../../../components/Settings/atom';
import CloseModalIcon from '../../../../assets/close-modal-icon.svg';

type TwygsBotSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  articleId: string;
};

const sampleQuestions = [
  'What are the key points I should know from this article?',
  'How does this perspective compare to others on this topic?',
  'What related topics might I want to explore next?'
];

const LoadingDots = () => (
  <Flex align="start" gap={3}>
    <Avatar src={TwygsLogoIcon} size="sm" bg="green.50" className="mt-[10px]" />
    <Box className="flex items-center">
      <Text className="px-4 py-2 rounded-2xl bg-white text-gray-600 animate-pulse">•••</Text>
    </Box>
  </Flex>
);

export default function TwygsBotSidebar({ isOpen, onClose, articleId }: TwygsBotSidebarProps) {
  const [profile] = useAtom(profileAtom);
  const [question, setQuestion] = useState('');
  const [questionInput, setQuestionInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messageContainer, setMessageContainer] = useState<Array<any>>([]);
  const messageContainerRef = useRef(null);
  const [activeEnter, setActiveEnter] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  };

  const UserQuestionComponent = (message) => (
    <Flex align="start" justifyContent="right" gap={3}>
      <Text
        className="px-[20px] py-[5px] rounded-[20px] bg-growth-green-100"
        fontWeight="400"
        fontSize="16px"
        color="gray.800"
      >
        {message}
      </Text>
      <Avatar src={profile.imgUrl || UserAvatar} size="sm" />
    </Flex>
  );

  const BotAnswerComponent = (message) => (
    <Flex align="start" gap={3}>
      <Avatar src={TwygsLogoIcon} size="sm" bg="green.50" className="mt-[10px]" />
      <Box>
        <LexicalViewerTwygsBot rawMarkdown={message} />
      </Box>
    </Flex>
  );

  useEffect(() => {
    scrollToBottom();
  }, [messageContainer]);

  useEffect(() => {
    if (questionInput.length) setActiveEnter(true);
    else setActiveEnter(false);
  }, [questionInput]);

  const handleSubmitQuestion = async (userPrompt: string) => {
    try {
      if (userPrompt.trim() === '') return;

      setQuestionInput('');
      setQuestion('');
      if (userPrompt === '') return;

      const userId = JSON.parse(localStorage.getItem('user'))?.id;
      if (!userId) throw new Error('Not found user');

      let incomingContainer = [...messageContainer, UserQuestionComponent(userPrompt)];
      setMessageContainer(incomingContainer);

      setIsLoading(true);
      await conserve({
        articleId,
        message: userPrompt
      }).then((data) => {
        let incomingContainer = [
          ...messageContainer,
          UserQuestionComponent(userPrompt),
          BotAnswerComponent(data?.explanation)
        ];
        setMessageContainer(incomingContainer);
      });
    } catch (error) {
      console.error('Error calling AI service:', error);
      const isLimited = error?.response?.data?.message?.search('maximum') > 0;
      setShowErrorModal(isLimited);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      blockScrollOnMount={false}
      trapFocus={false}
      size={'lg'}
    >
      <DrawerContent pb="env(safe-area-inset-bottom)" bg="#edf7ee" w={'100%'}>
        {/* Header */}
        <DrawerHeader p={4} m={0} maxW={'unset'}>
          <HStack justify="space-between" pb={2} borderBottom={'1px solid #D1C8C4'} w={'100%'}>
            <Flex align="center" gap={2}>
              <Image src={TwygsLogoIcon} alt="Twygsbot" h="24px" />
              <Text fontWeight="bold" fontSize="20" className="text-growth-green-500">
                Twygsbot
              </Text>
            </Flex>
            <img
              src={CloseModalIcon}
              onClick={onClose}
              className="h-9 w-9 cursor-pointer"
              color="growth-green"
              alt=""
            />
          </HStack>
        </DrawerHeader>

        {/* Body */}
        <DrawerBody p={4} ref={messageContainerRef}>
          {messageContainer.length === 0 ? (
            // Pre-question state
            <VStack align="stretch" spacing={6}>
              {/* Greeting Section */}
              <Box>
                <Text fontWeight="800" fontSize="lg" color="gray.800">
                  Hello, {profile.firstName || ''}
                </Text>
                <Text fontWeight="500" fontSize="s" className="text-jet-black">
                  How can I help you today?
                </Text>
              </Box>

              {/* Common Questions */}
              <Box>
                <Text fontWeight="500" fontSize="s" className="text-jet-black" mb={2}>
                  Here are some common questions you can try:
                </Text>
                <VStack align="stretch" spacing={3}>
                  {sampleQuestions.map((question) => (
                    <p
                      key={question}
                      onClick={() => {
                        setQuestion(question);
                        handleSubmitQuestion(question);
                      }}
                      className="cursor-pointer w-fit-content text-[12px] md:text-[16px] text-left rounded-full px-[16px] py-[8px] bg-growth-green-100 
                      border-[1px] border-growth-green-900
                      font-[600] text-growth-green-900
                      "
                    >
                      {question}
                    </p>
                  ))}
                </VStack>
              </Box>

              {/* Question Input */}
              <Box mb="auto">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitQuestion(question);
                  }}
                >
                  <Flex gap={2} className="relative">
                    <Input
                      onChange={(e) => {
                        setQuestion(e.target.value);
                        setQuestionInput(e.target.value);
                      }}
                      placeholder="Ask a question"
                      bg="white"
                      h={'52px'}
                      borderRadius={'80px'}
                      _focus={{
                        borderColor: 'var(--growth-green-100)',
                        boxShadow: '0 0 0 1px var(--chakra-colors-green-500)'
                      }}
                      pr="100px" /* Add space for the button */
                      pl="4" /* Small padding on the left */
                    />
                    <button
                      disabled={isLoading}
                      className={`absolute hover:scale-10 z-10 top-[8px] right-[20px] h-[36px] w-[63px] bg-white border-2 ${activeEnter ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-[#F2F3F7] text-[#CCCCCC]'} rounded-[8px] text-[14px] font-[700]`}
                    >
                      Enter
                    </button>
                  </Flex>
                </form>
                <div className="mt-[16px] text-justify font-500 text-[12px]">
                  Responses by Twygsbot may display inaccurate info, so be sure to double-check on
                  responses and sources.
                </div>
              </Box>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={6}>
              {messageContainer.map((message) => message)}
              {isLoading && <LoadingDots />}

              {/* Question Input */}
              <Box mb="auto" position={'sticky'} bottom={'-16px'} bgColor={'#edf7ee'}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitQuestion(question);
                  }}
                >
                  {showErrorModal ? (
                    <Text className="rounded-[4px] border-[1px] border-error-red bg-[#FFF6F6] text-center p-[8px] text-[16px] text-error-red font-[700] my-[24px]">
                      You’ve reached the daily limit of messages per 24 hours. Please try again
                      later.
                    </Text>
                  ) : (
                    <Flex gap={2} className="relative">
                      <Input
                        value={questionInput}
                        onChange={(e) => {
                          setQuestionInput(e.target.value);
                          setQuestion(e.target.value);
                        }}
                        placeholder="Ask a question"
                        bg="white"
                        h={'52px'}
                        borderRadius={'80px'}
                        _focus={{
                          borderColor: 'var(--growth-green-100)',
                          boxShadow: '0 0 0 1px var(--chakra-colors-green-500)'
                        }}
                        pr="100px" /* Add space for the button */
                        pl="4" /* Small padding on the left */
                      />
                      <button
                        disabled={isLoading}
                        className={`absolute hover:scale-10 z-10 top-[8px] right-[20px] h-[36px] w-[63px] bg-white border-2 ${activeEnter ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-[#F2F3F7] text-[#CCCCCC]'} rounded-[8px] text-[14px] font-[700]`}
                      >
                        Enter
                      </button>
                    </Flex>
                  )}
                </form>
                <div className="mt-[16px] text-justify font-500 text-[12px]">
                  Responses by Twygsbot may display inaccurate info, so be sure to double-check on
                  responses and sources.
                </div>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
